import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FileText, Send, ArrowDown, CheckCircle2, Zap } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85";

function CountUp({ to, decimals = 0, duration = 1.8, delay = 1 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now() + delay * 1000;
    const tick = (t) => {
      const p = Math.min(Math.max((t - start) / (duration * 1000), 0), 1);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, delay]);
  return <span>{v.toFixed(decimals)}</span>;
}

const HUD_ROWS = [
  { label: "Inventory Record Accuracy", value: 99.4, decimals: 1, suffix: "%", pct: 99, tone: "#00F0FF" },
  { label: "Open Purchase Orders", value: 37, decimals: 0, suffix: "", pct: 62, tone: "#2563EB" },
  { label: "Cycle Counts · This Week", value: 128, decimals: 0, suffix: "", pct: 78, tone: "#3B82F6" },
  { label: "Stockout Risk Alerts", value: 0, decimals: 0, suffix: "", pct: 4, tone: "#10B981" },
];

const HEADLINE_LINES = [
  ["Modernizing ", <span key="a" className="text-[#00F0FF] text-glow">Inventory Control</span>],
  ["& Material Planning"],
  ["for Growing Operations"],
  ["in Poway & San Diego."],
];

export default function Hero() {
  const ref = useRef(null);
  const hudRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const hudY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const r = hudRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 5, ry: px * 7 });
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src={HERO_IMG}
          alt="Warehouse racking and inventory operations — Robert Daniels, inventory control and MRP specialist serving Poway and San Diego, CA"
          className="w-full h-[118%] object-cover img-duotone opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F17]/60 via-[#0B0F17]/85 to-[#0B0F17]" />
        <div className="absolute inset-0 bg-blueprint" />
        <div className="absolute -top-32 left-1/3 w-[560px] h-[560px] rounded-full bg-[#2563EB]/15 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#00F0FF]/10 blur-[130px]" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#00F0FF]"
          >
            <span className="h-2 w-2 rounded-full bg-[#10B981] status-dot" />
            Robert Daniels · Available for Contract &amp; Full-Time
          </motion.div>

          <h1 className="mt-6">
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block font-display font-extrabold tracking-tight text-4xl sm:text-6xl lg:text-[4.1rem] leading-[1.05] text-white"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.18 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed"
          >
            Helping small-to-mid-size manufacturers and distributors eliminate stockouts,
            streamline BOM management, and replace manual spreadsheets with robust,
            data-driven MRP workflows.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <motion.a
              href="#resume"
              data-testid="hero-cta-resume"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center gap-2.5 font-display font-semibold text-[#0B0F17] bg-[#00F0FF] hover:bg-[#4DF7FF] rounded-full px-7 py-4 shadow-[0_10px_40px_rgba(0,240,255,0.25)] transition-colors duration-300"
            >
              <FileText size={18} />
              View Professional Resume
            </motion.a>
            <motion.a
              href="#contact"
              data-testid="hero-cta-hire"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center gap-2.5 font-display font-semibold text-[#F9FAFB] border border-white/20 hover:border-[#00F0FF]/70 hover:bg-white/5 rounded-full px-7 py-4 transition-all duration-300"
            >
              <Send size={17} />
              Schedule a Consultation / Hire Me
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-[#6B7280] flex flex-wrap gap-x-6 gap-y-2"
          >
            <span>Poway, CA</span>
            <span>robdan316@gmail.com</span>
            <span>(858) 231-7380</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5" ref={hudRef} onMouseMove={onMove} onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}>
          <motion.div
            initial={{ opacity: 0, y: 44 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{ y: hudY, transform: `perspective(1100px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
            className="relative"
          >
            <div
              data-testid="hero-status-hud"
              className="relative rounded-2xl border border-white/10 bg-[#111827]/80 backdrop-blur-xl p-6 sm:p-7 shadow-[0_0_90px_rgba(0,240,255,0.08)]"
            >
              <div className="flex items-center justify-between gap-3 pb-5 border-b border-white/[0.07]">
                <div className="flex items-center gap-2.5">
                  <Zap size={14} className="text-[#00F0FF]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#D1D5DB]">
                    Materials Planning · Live Feed
                  </span>
                </div>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B7280] border border-white/10 rounded-full px-2.5 py-1">
                  Illustrative
                </span>
              </div>

              <div className="pt-5 space-y-5">
                {HUD_ROWS.map((row, i) => (
                  <div key={row.label} data-testid={`hud-row-${i}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[13px] text-[#9CA3AF]">{row.label}</span>
                      <span className="font-mono text-lg font-semibold" style={{ color: row.tone }}>
                        <CountUp to={row.value} decimals={row.decimals} delay={1.1 + i * 0.18} />
                        {row.suffix}
                      </span>
                    </div>
                    <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: row.tone }}
                        initial={{ width: 0 }}
                        animate={{ width: `${row.pct}%` }}
                        transition={{ duration: 1.4, delay: 1.15 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <p className="pt-5 mt-5 border-t border-white/[0.07] font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                Systems view · ERP (Misys) · QuickBooks — demo feed
              </p>
            </div>

            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="hidden sm:flex absolute -top-5 -right-2 lg:-right-6 items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#0B0F17]/90 backdrop-blur px-4 py-2 shadow-[0_8px_30px_rgba(16,185,129,0.15)]"
            >
              <CheckCircle2 size={13} className="text-[#10B981]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#D1D5DB]">BOM Rev 14 · Synced</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#value"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#6B7280] hover:text-[#00F0FF] transition-colors"
        aria-label="Scroll to next section"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={15} />
        </motion.span>
      </motion.a>
    </section>
  );
}
